import React, { useState, useEffect } from 'react';
import { useAppStore } from '@/shared/store/appStore';
import { getImageUrl } from '@/shared/utils/imageUrl';

/**
 * CinescopeLogo component rendering the authentic brand logo image if available,
 * or clean "CINESCOPE" typography wordmark as fallback.
 *
 * @param {Object} props
 * @param {string} [props.className=''] - Additional custom CSS classes for outer container
 * @param {string} [props.imgClassName=''] - Custom CSS classes on the logo image element
 * @param {string} [props.textColor='text-zinc-900'] - CSS color class for the Cinescope text
 * @param {boolean} [props.showSubtitle=false] - Whether to show the subtitle text on fallback
 * @param {string} [props.subtitle="Specialist for Film and TV Production Gear"] - Subtitle content
 */
export default function CinescopeLogo({
  className = '',
  imgClassName = '',
  textColor = 'text-zinc-900',
  showSubtitle = false,
  subtitle = "Specialist for Film and TV Production Gear"
}) {
  const [darkImgFailed, setDarkImgFailed] = useState(false);
  const [lightImgFailed, setLightImgFailed] = useState(false);

  const siteLogoDark = useAppStore((state) => state.siteSettings?.site_logo_dark);
  const siteLogoLight = useAppStore((state) => state.siteSettings?.site_logo_light);
  const siteLogoDefault = useAppStore((state) => state.siteSettings?.site_logo);

  // If text-white (transparent dark header or dark footer), we render logo for dark background. 
  // Otherwise (scrolled white navbar or light background), we render logo for light background.
  const isDarkBg = textColor.includes('text-white') || textColor.includes('white');

  // Compute resolved URLs for dark and light logo variants
  const customDarkUrl = (siteLogoDark || siteLogoDefault) ? getImageUrl(siteLogoDark || siteLogoDefault) : null;
  const customLightUrl = (siteLogoLight || siteLogoDefault) ? getImageUrl(siteLogoLight || siteLogoDefault) : null;

  const showDarkImg = Boolean(customDarkUrl && !darkImgFailed);
  const showLightImg = Boolean(customLightUrl && !lightImgFailed);

  // Reset failure state if store settings change
  useEffect(() => {
    setDarkImgFailed(false);
  }, [siteLogoDark, siteLogoDefault]);

  useEffect(() => {
    setLightImgFailed(false);
  }, [siteLogoLight, siteLogoDefault]);

  const defaultImgClasses = "h-9 md:h-11 w-auto object-contain";
  const resolvedImgClass = imgClassName || defaultImgClasses;

  const hasActiveImage = isDarkBg ? showDarkImg : showLightImg;

  return (
    <div className={`flex items-center ${className} select-none`}>
      {/* ─── Dual Preloaded Custom Logo Images (if uploaded in site settings) ─── */}
      {showDarkImg && (
        <img
          src={customDarkUrl}
          alt="Cinescope"
          width="160"
          height="44"
          fetchPriority="high"
          decoding="async"
          className={`${resolvedImgClass} transition-opacity duration-200 ${
            isDarkBg ? 'block opacity-100' : 'hidden opacity-0'
          }`}
          onError={() => setDarkImgFailed(true)}
        />
      )}

      {showLightImg && (
        <img
          src={customLightUrl}
          alt="Cinescope"
          width="160"
          height="44"
          fetchPriority="high"
          decoding="async"
          className={`${resolvedImgClass} transition-opacity duration-200 ${
            !isDarkBg ? 'block opacity-100' : 'hidden opacity-0'
          }`}
          onError={() => setLightImgFailed(true)}
        />
      )}

      {/* ─── Clean Typography Wordmark (Only "CINESCOPE" text, no camera SVG) ─── */}
      {!hasActiveImage && (
        <div className="flex flex-col justify-center text-left">
          <span className={`font-display font-black tracking-[0.06em] uppercase text-lg md:text-xl leading-none ${textColor}`}>
            Cinescope
          </span>
          {showSubtitle && (
            <span className={`text-[6.5px] md:text-[7px] font-mono ${textColor} opacity-80 font-bold uppercase tracking-[0.08em] mt-1.5 leading-none block`}>
              {subtitle}
            </span>
          )}
        </div>
      )}
    </div>
  );
}
